import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Check, Send, Plus, Bold, Italic, Strikethrough, Link2, Smile, AtSign } from 'lucide-react';
// Authentic four-color Slack mark, inlined as SVG so the composer reads
// unmistakably as Slack (the icon CDN only serves a flat monochrome version).
function SlackMark({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-2963" data-mp="ec7lwawasl5">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-2964" data-mp="3rsqajpw6al9i" />

      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-2965" data-mp="3iq8q9h02w2p5" />

      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-2966" data-mp="2stqcyo1wqluj" />

      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-2967" data-mp="2oty1a2h7vzky" />
    </svg>;
}
import { WaitlistCTA } from './WaitlistCTA';
import { useSuppressFloatingCta } from './floatingCtaContext';
import { useReducedMotion } from 'framer-motion';
const BACKGROUND_VIDEO_URL = "/download (11).mp4";
// Brand logo with the dual-CDN fallback used across the home page.
function BrandLogo({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.simpleicons.org/${slug}`} alt="" aria-hidden="true" className={className} loading="lazy" onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.dataset.fallback) {
      img.dataset.fallback = '1';
      img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-2968" data-mp="1c2w9kjdnpk7t" />;
}
// Connect — a real integrations settings panel. Two apps already connected
// (green status pills, the familiar pattern) and one with the actionable
// orange "Connect" button highlighted, so it reads as a specific button you'd
// click in a real settings screen rather than a decorative orbit of icons.
function ConnectVisual() {
  const rows = [{
    slug: 'slack',
    name: 'Slack',
    connected: true
  }, {
    slug: 'gmail',
    name: 'Gmail',
    connected: true
  }, {
    slug: 'notion',
    name: 'Notion',
    connected: false
  }];
  return <div className="relative w-full h-full flex items-center justify-center px-5 overflow-hidden" data-id="element-2969" data-mp="29ok6r6hxs6ke">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,88,12,0.05)_0%,transparent_70%)]" data-id="element-2970" data-mp="2h8364nbhmcf5" />
      <div className="relative w-full max-w-[230px] rounded-xl bg-white border border-neutral-200/70 shadow-[0_12px_30px_-16px_rgba(43,33,26,0.3)] overflow-hidden" data-id="element-2971" data-mp="2qzz8wboiqqpp">
        {/* Panel header — reads like a real "Integrations" settings card. */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100" data-id="element-2972" data-mp="1ycmifqf8lyr4">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-2973" data-mp="23aod78m0zmif">
            Integrations
          </span>
          <span className="text-[8px] font-semibold text-neutral-400" data-id="element-2974" data-mp="287xycc2dtc0g">
            2 connected
          </span>
        </div>
        <div className="divide-y divide-neutral-100" data-id="element-2975" data-mp="2c8hzdqq2szrv">
          {rows.map((row, i) => <motion.div key={row.slug} initial={{
          opacity: 0,
          x: -6
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1,
          duration: 0.35
        }} className="flex items-center gap-2 px-3 py-2" data-id="element-2976" data-mp="3jo4u0zjhihq2" data-mpi={i}>
              <span className="w-6 h-6 rounded-md bg-[#FAF8F4] border border-neutral-200/70 flex items-center justify-center shrink-0" data-id="element-2977" data-mp="1maxmqqqrp5">
                <BrandLogo slug={row.slug} className="w-3.5 h-3.5 object-contain" data-id="element-2978" data-mp="3r6vuavk8c7ps" />
              </span>
              <span className="text-[10px] font-semibold text-neutral-700 flex-1 truncate" data-id="element-2979" data-mp="90zpuuxmw8h9">
                {row.name}
              </span>
              {row.connected ? <span className="flex items-center gap-1 text-[8px] font-bold text-[#0F9D58]" data-id="element-2980" data-mp="is40jiga176a">
                  <Check size={10} strokeWidth={3.5} data-id="element-2981" data-mp="dt0v3wwdwyxs" />
                  Connected
                </span> :
          // The one actionable affordance — a real, obvious "Connect"
          // button, gently pulsing to draw the eye.
          <motion.span className="text-[8px] font-bold text-white bg-[#EA580C] rounded-md px-2 py-1 shadow-sm" animate={{
            scale: [1, 1.06, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2982" data-mp="nrzx4wmn9wbt">
                  Connect
                </motion.span>}
            </motion.div>)}
        </div>
      </div>
    </div>;
}
// Ask — a real Slack-style message composer with the prompt typed in, a styled
// @Giga mention, a faux blinking caret, and a real send button. Reads like a
// specific input you'd type into and a specific button you'd press, not an
// abstract bubble.
function AskVisual() {
  return <div className="relative w-full h-full flex items-center justify-center px-5 overflow-hidden" data-id="element-2983" data-mp="eol01b24la0p">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(43,33,26,0.03)_0%,transparent_70%)]" data-id="element-2984" data-mp="ipn1zznfg06d" />
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4
    }} className="relative w-full max-w-[238px] rounded-xl bg-white border border-neutral-200/70 shadow-[0_12px_30px_-16px_rgba(43,33,26,0.3)] overflow-hidden" data-id="element-2985" data-mp="4r6mpkbi11cm">
        {/* Slack channel header (aubergine) — the four-color Slack mark + the
         open channel name + a Slack-style member count, so it reads
         unmistakably as the Slack app, not a generic chat box. */}
        <div className="flex items-center gap-1.5 bg-[#3F0E40] px-3 py-2" data-id="element-2986" data-mp="2ud9rnx403yxf">
          <span className="flex items-center justify-center w-4 h-4 rounded bg-white shrink-0" data-id="element-2987" data-mp="2ub5r9p5b9i1w">
            <SlackMark className="w-2.5 h-2.5" data-id="element-2988" data-mp="2q9k85m26o7j9" />
          </span>
          <span className="text-[10px] font-bold text-white truncate" data-id="element-2989" data-mp="2lbirf7s8ckay">
            <span className="text-white/55" data-id="element-2990" data-mp="2ge0dt439pgwm">#</span> sales
          </span>
          <span className="ml-auto flex items-center gap-1 text-[8px] font-semibold text-white/55 shrink-0" data-id="element-2991" data-mp="2gfuychamn706">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2EB67D]" data-id="element-2992" data-mp="14ybr8dtxejwc" />
            8
          </span>
        </div>

        {/* Slack message composer — formatting toolbar across the top, the
         message with a styled @Giga mention in the middle, and the action row
         with Slack's green send button at the bottom. Mirrors the real Slack
         composer layout. */}
        <div className="m-2.5 rounded-lg border border-neutral-300/80 overflow-hidden" data-id="element-2993" data-mp="14qq7xxt5st46">
          {/* Formatting toolbar — Slack's signature B / I / S / link row. */}
          <div className="flex items-center gap-2.5 border-b border-neutral-200 px-2.5 py-1.5 text-neutral-400" data-id="element-2994" data-mp="1dkczfbqc3a7k">
            <Bold size={11} strokeWidth={2.75} data-id="element-2995" data-mp="1im2er3klaa1g" />
            <Italic size={11} strokeWidth={2.75} data-id="element-2996" data-mp="1nistnnknqzla" />
            <Strikethrough size={11} strokeWidth={2.75} data-id="element-2997" data-mp="1ngzl22qhi5an" />
            <span className="h-3 w-px bg-neutral-200" data-id="element-2998" data-mp="1sh433x71j82z" />
            <Link2 size={11} strokeWidth={2.5} data-id="element-2999" data-mp="1sfsxrsxy5s9r" />
          </div>

          {/* The message text */}
          <p className="px-2.5 pt-2 text-[11px] text-neutral-800 leading-snug" data-id="element-3000" data-mp="3u9ij2qq0y2gr">
            <span className="font-semibold text-[#1264A3] bg-[#1264A3]/10 rounded px-0.5" data-id="element-3001" data-mp="3ubmlf489ytex">
              @Giga
            </span>{' '}
            update the Acme deal to Closed Won and email the team
            {/* Blinking caret */}
            <motion.span aria-hidden="true" className="inline-block w-px h-3 -mb-0.5 ml-0.5 bg-[#1264A3] align-middle" animate={{
            opacity: [1, 1, 0, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-3002" data-mp="29r7xyskyy2cf" />
          </p>

          {/* Action row — Slack's left-hand composer icons and the green send
           button it shows once there's a message to send. */}
          <div className="flex items-center justify-between px-2.5 pb-1.5 pt-2" data-id="element-3003" data-mp="1q51vt2kkvphn">
            <span className="flex items-center gap-2 text-neutral-400" data-id="element-3004" data-mp="1m0u3ywitptf7">
              <Plus size={12} strokeWidth={2.5} data-id="element-3005" data-mp="31i4dk0yeyl3e" />
              <Smile size={12} strokeWidth={2.5} data-id="element-3006" data-mp="31gucr406eyt2" />
              <AtSign size={12} strokeWidth={2.5} data-id="element-3007" data-mp="2xhlkk3ksb8zm" />
            </span>
            <motion.span className="flex items-center justify-center w-6 h-6 rounded-md bg-[#007A5A] shadow-sm" animate={{
            scale: [1, 1.08, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-3008" data-mp="2xjepnefr1l5i">
              <Send size={11} strokeWidth={2.5} className="text-white" data-id="element-3009" data-mp="2v4i0n01nqvug" />
            </motion.span>
          </div>
        </div>
      </motion.div>
    </div>;
}
// Done — the actual results from the apps, tied to the exact prompt in step 02.
// A Salesforce-style deal record flipping its stage badge to "Closed Won", and
// a Gmail-style "sent" confirmation. Real product results, not generic pills.
function DoneVisual() {
  return <div className="relative w-full h-full flex flex-col items-center justify-center gap-2.5 px-5 overflow-hidden" data-id="element-3010" data-mp="3j0clul1vv129">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(15,157,88,0.04)_0%,transparent_70%)]" data-id="element-3011" data-mp="ak0yygr6duzk" />

      {/* Salesforce deal record — stage flipped to Closed Won */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4
    }} className="relative z-10 w-full max-w-[230px] rounded-lg bg-white border border-neutral-200/70 shadow-[0_10px_26px_-16px_rgba(43,33,26,0.3)] overflow-hidden" data-id="element-3012" data-mp="3o1dip32qvtan">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-neutral-100" data-id="element-3013" data-mp="3j0g38y94ddcu">
          <BrandLogo slug="salesforce" className="w-3 h-3 object-contain" data-id="element-3014" data-mp="34bjx56qmiaev" />
          <span className="text-[8px] font-bold text-neutral-400" data-id="element-3015" data-mp="394v4d8zckt7b">
            Salesforce · Opportunity
          </span>
        </div>
        <div className="flex items-center justify-between px-2.5 py-2" data-id="element-3016" data-mp="2ve9irciie55q">
          <div className="min-w-0" data-id="element-3017" data-mp="30cb2lznre8i9">
            <div className="text-[10px] font-bold text-neutral-800 leading-tight truncate" data-id="element-3018" data-mp="309f0dz4voxo0">
              Acme Corp
            </div>
            <div className="text-[8px] text-neutral-400" data-id="element-3019" data-mp="2qdv8dntfqj4j">$84,000 · Q3</div>
          </div>
          <span className="flex items-center gap-1 text-[8px] font-bold text-[#0F9D58] bg-[#0F9D58]/10 rounded-full px-2 py-1 shrink-0" data-id="element-3020" data-mp="26ra6fr64wmkp">
            <Check size={9} strokeWidth={3.5} data-id="element-3021" data-mp="22r9ss8qv0fep" />
            Closed Won
          </span>
        </div>
      </motion.div>

      {/* Gmail sent confirmation */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.18,
      duration: 0.4
    }} className="relative z-10 w-full max-w-[230px] rounded-lg bg-white border border-neutral-200/70 shadow-[0_10px_26px_-16px_rgba(43,33,26,0.3)] px-2.5 py-2 flex items-center gap-2" data-id="element-3022" data-mp="2bqwlm65iwcw5">
        <span className="w-6 h-6 rounded-md bg-[#FAF8F4] border border-neutral-200/70 flex items-center justify-center shrink-0" data-id="element-3023" data-mp="2uh4gy22zdz1s">
          <BrandLogo slug="gmail" className="w-3.5 h-3.5 object-contain" data-id="element-3024" data-mp="39dlqqoviv5m8" />
        </span>
        <div className="min-w-0 flex-1" data-id="element-3025" data-mp="34ftzzy6m5pjm">
          <div className="text-[9.5px] font-bold text-neutral-800 leading-tight truncate" data-id="element-3026" data-mp="34h5644nj79dw">
            Re: Acme — Closed Won 🎉
          </div>
          <div className="text-[8px] text-neutral-400 truncate" data-id="element-3027" data-mp="3ghbtf0zt0mbs">
            To: sales@yourco.com
          </div>
        </div>
        <span className="flex items-center gap-1 text-[8px] font-bold text-[#0F9D58] shrink-0" data-id="element-3028" data-mp="3lfn41dxxb0v2">
          <Check size={9} strokeWidth={3.5} data-id="element-3029" data-mp="3q8g9k417ud87" />
          Sent
        </span>
      </motion.div>
    </div>;
}
const STEPS = [{
  n: '01',
  title: 'Connect',
  body: 'Add Giga to Slack or Teams, then link the tools you already run on — one-click OAuth, no admin.',
  visual: <ConnectVisual data-id="element-3030" data-mp="81s3obycdm1h" />
}, {
  n: '02',
  title: 'Ask',
  body: 'Say what you want in plain English — the outcome, not the steps. Giga figures out the rest.',
  visual: <AskVisual data-id="element-3031" data-mp="d03fgs8766mc" />
}, {
  n: '03',
  title: 'Done',
  body: 'Giga does the work across your stack and reports back — once, or on a schedule.',
  visual: <DoneVisual data-id="element-3032" data-mp="2erpyo7lukx89" />
}];
export function GettingStartedSteps() {
  const reducedMotion = !!useReducedMotion();
  // While this section's own "Get started for free" CTA is on screen, suppress
  // the global floating "Try Giga for free" button so the two don't stack /
  // overlap on mobile.
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, {
    margin: '0px 0px -10% 0px'
  });
  useSuppressFloatingCta(ctaInView);
  return <section className="relative w-full bg-[#F4F0EA] py-24 md:py-32 overflow-hidden border-y border-neutral-200/70" data-id="element-3033" data-mp="1slnixylsv335">
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3034" data-mp="2q58fl7rawdwr">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-16" data-id="element-3035" data-mp="2l6ngjbnsc09w">
          <h2 className="text-4xl md:text-5xl font-title text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6 font-[500]" data-id="element-3036" data-mp="2g7u3fkceuba9">
            {/* On mobile, break before the duration so "2 minutes" stays
             together on its own second line instead of orphaning "minutes".
             Desktop flows naturally on one line. */}
            Up and running in <br className="md:hidden" data-id="element-3037" data-mp="2oot00hntf6do" />
            <span className="whitespace-nowrap" data-id="element-3038" data-mp="2onrokc2w0hvf">2 minutes</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl md:mx-auto" data-id="element-3039" data-mp="2th4c0ivtyiml">
            No setup project. No IT ticket. <br className="md:hidden" data-id="element-3040" data-mp="2ep272kewkzfp" />
            No training videos.
          </p>
        </div>

        {/* Steps — one bordered panel, three cells with hairline dividers. */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-2xl md:rounded-[2rem] border border-neutral-200/70 bg-white/40 grid grid-cols-1 md:grid-cols-3 mb-14 md:mb-16 overflow-hidden" data-id="element-3041" data-mp="37gdfchyxti95">
          {STEPS.map((step, i) => <div key={step.n} className={`flex flex-col p-8 md:p-10 ${i < STEPS.length - 1 ? 'border-b md:border-b-0 md:border-r border-neutral-200/70' : ''}`} data-id="element-3042" data-mp="1r1c8neq3zs9z" data-mpi={i}>
              {/* Text Area */}
              <div className="flex-1 mb-8" data-id="element-3043" data-mp="454l7r70leul">
                {/* Numbered eyebrow with the orange tick. */}
                <div className="flex items-center gap-3 mb-6" data-id="element-3044" data-mp="i4dsg1d8symf">
                  <span className="font-mono text-sm font-bold tracking-[0.2em] text-[#EA580C]" data-id="element-3045" data-mp="i2jdd3onk0q9">
                    {step.n}
                  </span>
                  <span aria-hidden="true" className="h-px w-7 bg-[#EA580C]" data-id="element-3046" data-mp="3mi2gvlwrxliy" />
                </div>

                <h3 className="text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-3047" data-mp="3hkk5zpz74w8b">
                  {step.title}
                </h3>
                <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium max-w-xs" data-id="element-3048" data-mp="3qkgzc4wj6j9p">
                  {step.body}
                </p>
              </div>

              {/* Visual Area - Inside container under the text */}
              <div className="h-[200px] rounded-2xl bg-[#FAF8F4] border border-neutral-200/60 overflow-hidden shrink-0" data-id="element-3049" data-mp="2yshu0rxi244e">
                {step.visual}
              </div>
            </div>)}
        </motion.div>

        {/* CTA */}
        <div ref={ctaRef} className="flex flex-col items-center gap-4" data-id="element-3050" data-mp="16pn8koy6me98">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-3051" data-mp="1bjrf573jck9s">
            Get started for free
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-3052" data-mp="ntx5eyeijbtr" />
          </WaitlistCTA>
        </div>
      </div>
    </section>;
}